import { zodResolver } from '@hookform/resolvers/zod';
import { ForwardRefRenderFunction, forwardRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StopRegistrySignContentType } from '../../../../../generated/graphql';
import { StopWithDetails } from '../../../../../types';
import { mapSignContentTypeToUiName } from '../../../../../utils/i18n';
import { EnumDropdown } from '../../../../common/Dropdowns';
import {
  FormActionButtons,
  ReasonForChangeForm,
} from '../../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../../common/Forms/NavigationBlocker';
import { InputField, InputLabel } from '../../../../common/Inputs';
import {
  Column,
  FormColumn,
  HorizontalSeparator,
  Row,
} from '../../../../common/LayoutComponents';
import { LocationTerminalDetails } from './LocationTerminalDetails';
import { LocationDetailsFormState, locationDetailsFormSchema } from './schema';
import { LocationSignContentType } from './types';

const testIds = {
  streetAddress: 'LocationDetailsForm::streetAddress',
  postalCode: 'LocationDetailsForm::postalCode',
  municipality: 'LocationDetailsForm::municipality',
  fareZone: 'LocationDetailsForm::fareZone',
  latitude: 'LocationDetailsForm::latitude',
  longitude: 'LocationDetailsForm::longitude',
  altitude: 'LocationDetailsForm::altitude',
  functionalArea: 'LocationDetailsForm::functionalArea',
  platformNumber: 'LocationDetailsForm::platformNumber',
  signContentType: 'LocationDetailsForm::signContentType',
};

type LocationDetailsFormComponentProps = {
  readonly className?: string;
  readonly defaultValues: Partial<LocationDetailsFormState>;
  readonly onSubmit: (state: LocationDetailsFormState) => void;
  readonly stop: StopWithDetails;
  readonly onCancel: () => void;
  readonly testIdPrefix: string;
};

const LocationDetailsFormComponent: ForwardRefRenderFunction<
  ExplicitAny,
  LocationDetailsFormComponentProps
> = (
  { className, defaultValues, onSubmit, stop, onCancel, testIdPrefix },
  ref,
) => {
  const { t } = useTranslation();

  const methods = useForm<LocationDetailsFormState>({
    defaultValues,
    resolver: zodResolver(locationDetailsFormSchema),
  });
  useDirtyFormBlockNavigation(methods.formState, 'LocationDetailsForm');
  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form className={className} onSubmit={handleSubmit(onSubmit)} ref={ref}>
        <LocationTerminalDetails stop={stop} />
        <FormColumn>
          <Row className="mt-5 flex-wrap gap-4">
            <InputField<LocationDetailsFormState>
              type="text"
              translationPrefix="stopDetails.location"
              fieldPath="streetAddress"
              testId={testIds.streetAddress}
            />
            <InputField<LocationDetailsFormState>
              type="text"
              inputClassName="w-24"
              translationPrefix="stopDetails.location"
              fieldPath="postalCode"
              testId={testIds.postalCode}
            />
            <Column>
              <InputLabel
                fieldPath="municipality"
                translationPrefix="stopDetails.location"
              />
              <div
                className="flex h-full items-center"
                title={t(($) => $.stopDetails.location.municipalityInputNote)}
              >
                <span
                  id="stopDetails.location.municipality"
                  data-testid={testIds.municipality}
                >
                  {
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    stop.stop_place?.municipality || '-'
                  }
                </span>
                <i className="icon-info mx-2 text-lg text-brand" />
              </div>
            </Column>
            <Column>
              <InputLabel
                fieldPath="fareZone"
                translationPrefix="stopDetails.location"
              />
              <div
                className="flex h-full items-center"
                title={t(($) => $.stopDetails.location.fareZoneInputNote)}
              >
                <span
                  id="stopDetails.location.fareZone"
                  data-testid={testIds.fareZone}
                >
                  {
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    stop.stop_place?.fareZone || '-'
                  }
                </span>
                <i className="icon-info mx-2 text-lg text-brand" />
              </div>
            </Column>
          </Row>
          <Row className="flex-wrap gap-4">
            <InputField<LocationDetailsFormState>
              type="number"
              inputClassName="w-36"
              translationPrefix="stopDetails.location"
              disabled
              size={3}
              fieldPath="latitude"
              testId={testIds.latitude}
            />
            <InputField<LocationDetailsFormState>
              type="number"
              inputClassName="w-36"
              translationPrefix="stopDetails.location"
              disabled
              fieldPath="longitude"
              testId={testIds.longitude}
            />
            <InputField<LocationDetailsFormState>
              type="number"
              inputClassName="w-32"
              translationPrefix="stopDetails.location"
              disabled
              fieldPath="altitude"
              testId={testIds.altitude}
            />
            <InputField<LocationDetailsFormState>
              type="number"
              inputClassName="w-32"
              translationPrefix="stopDetails.location.input"
              min={0}
              fieldPath="functionalArea"
              testId={testIds.functionalArea}
            />
          </Row>
          <HorizontalSeparator />
          <Row className="flex-wrap gap-4">
            <InputField<LocationDetailsFormState>
              type="text"
              translationPrefix="stopDetails.location"
              fieldPath="platformNumber"
              testId={testIds.platformNumber}
            />
            <InputField<LocationDetailsFormState>
              translationPrefix="stopDetails.location"
              fieldPath="signContentType"
              testId={testIds.signContentType}

              inputElementRenderer={(props) => (
                <EnumDropdown<StopRegistrySignContentType>
                  enumType={LocationSignContentType}
                  placeholder={t(($) => $.signContentTypeEnum.none)}
                  uiNameMapper={(value) => mapSignContentTypeToUiName(t, value)}
                  buttonClassName="min-w-36"
                  includeNullOption
                  {...props}
                />
              )}
            />
          </Row>

          <HorizontalSeparator />

          <ReasonForChangeForm />
        </FormColumn>
        <FormActionButtons
          onCancel={onCancel}
          testIdPrefix={testIdPrefix}
          isDisabled={
            !methods.formState.isDirty || methods.formState.isSubmitting
          }
          isSubmitting={methods.formState.isSubmitting}
          variant="infoContainer"
        />
      </form>
    </FormProvider>
  );
};

export const LocationDetailsForm = forwardRef(LocationDetailsFormComponent);
